import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { isNumber } from 'lodash-es';
import LottieView from 'lottie-react-native';
import { LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';

import { maybePlayAudioOnce } from 'components/app-takeover/play-audio-once';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import Picture from 'components/picture';
import { toaster } from 'components/toaster/toaster';
import { onboardingIsLikelyCompleteDeferred } from 'state/onboarding/hooks/use-onboarding-content';
import { primitive } from 'styles/constants/primitives';
import { Deferred } from 'utils/Deferred';
import { isWeb } from 'utils/environment';
import { EventName, emitEvent } from 'utils/event-hub';
import { animatedSplashScreenHiddenDeferred } from 'utils/hide-splash-screen';

import { CTAButtons } from '../components/cta-button';
import { ISimpleHomePageProps } from '../types';
import { sendHeroClickEvent } from '../utils';

const rgbColorMidpoint = Number('0x777777');

// this only needs to happen once per app load
const onImageLoadedDeferred = new Deferred();
if (__DEV__ && !isWeb) {
  onImageLoadedDeferred.resolve(); // live reload throws this off in dev
}
let didPlay = false;

const _SimpleHomePageMobile = ({
  navigate,
  numberOfItemsInCart,
  serviceMode,
  item,
  flatListDimensions,
}: ISimpleHomePageProps) => {
  const [safeAreaHeight, setSafeAreaHeight] = useState<Number | null>(null);
  const appTakeover = useAppTakeover();
  const {
    targetSimpleHomePageIds,
    containerBackgroundColor,
    audio: takeoverAudio,
    onLottieFinishedToastArgs: takeoverOnLottieFinishedToastArgs,
  } = appTakeover?.homePage || {};
  const isTakeoverActive = targetSimpleHomePageIds?.includes(item._id);

  const animationRef = useRef<LottieView>(null);

  const {
    mobileImage,
    mobileImageLottie,
    altImageText,
    _id,
    imageLink,
    mobileImageBackgroundColor,
    imageDisclaimerText,
    daypart,
  } = item;

  const imageAlt = altImageText?.locale ?? '';
  const image = mobileImage?.locale;
  const lottieUrl = mobileImageLottie?.locale?.asset?.url;

  const onImageLoaded = useCallback(() => {
    onImageLoadedDeferred.resolve();
    emitEvent(EventName.MARKETING_IMAGE_LOADED);
  }, []);

  useEffect(() => {
    if (!isTakeoverActive) {
      return;
    }

    Promise.all([
      onImageLoadedDeferred.promise, // waiting until the image is loaded until playing is required on web or it doesn't work
      animatedSplashScreenHiddenDeferred.promise, // don't play if splash screen is up
      onboardingIsLikelyCompleteDeferred.promise, // don't play if onboarding modal is up
    ]).then(() => {
      if (!didPlay) {
        maybePlayAudioOnce(takeoverAudio);
      }
      animationRef?.current?.play();
    });
  }, []);

  // reverse the disclaimer color to white-ish if the background color is dark (below 50% of RGB space)
  const reverseDisclaimerColor =
    mobileImageBackgroundColor &&
    imageDisclaimerText?.locale &&
    Number('0x' + mobileImageBackgroundColor.replace('#', '')) < rgbColorMidpoint;

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;
      // this sets a max height if the safe area of the image is going to get cut off horizontally
      // (this is extremely rare only with devices that are very tall and narrow)
      if (
        width &&
        isNumber(width) &&
        height &&
        isNumber(height) &&
        safeAreaHeight === null &&
        width / height < 0.625
      ) {
        // the aspect ratio of the image safe area is 1.5x the width
        setSafeAreaHeight(width * 1.5);
      }
    },
    [safeAreaHeight]
  );

  const handleOnPress = () => {
    if (imageLink) {
      sendHeroClickEvent({ imageLink, daypart });

      navigate(imageLink);
    }
  };

  const safeAreaStyle = safeAreaHeight ? { maxHeight: safeAreaHeight as any } : undefined;

  return (
    <View
      testID="simple-home-page"
      style={{
        height: flatListDimensions?.height,
        ...(isTakeoverActive ? { backgroundColor: containerBackgroundColor } : {}),
      }}
    >
      <Pressable
        style={[styles.pressable, { backgroundColor: mobileImageBackgroundColor || undefined }]}
        onPress={handleOnPress}
        aria-label={imageAlt}
      >
        {image && !lottieUrl && (
          <Picture
            priority="high"
            image={image}
            alt={imageAlt}
            onImageLoaded={onImageLoaded}
            style={[styles.image, safeAreaStyle]}
            onLayout={onLayout}
          />
        )}
        {lottieUrl && (
          <LottieView
            ref={animationRef}
            resizeMode="cover"
            hardwareAccelerationAndroid
            loop={false}
            style={[styles.lottie, safeAreaStyle]}
            // the mandatory spec of the lottie is 1536px. on web only hardcoding it to this scales correctly.
            webStyle={{ width: 1536 }}
            onAnimationLoaded={onImageLoaded}
            onAnimationFinish={() => {
              if (takeoverOnLottieFinishedToastArgs && isTakeoverActive && !didPlay) {
                toaster.richToast(takeoverOnLottieFinishedToastArgs);
              }
              didPlay = true;
            }}
            onLayout={onLayout}
            source={{ uri: lottieUrl }}
          />
        )}
        {imageDisclaimerText?.locale && (
          <DisclaimerText
            style={reverseDisclaimerColor ? { color: primitive.bk.$mayoEggWhite } : undefined}
          >
            {imageDisclaimerText?.locale}
          </DisclaimerText>
        )}
      </Pressable>
      <FooterContainer>
        <CTAButtons
          numberOfItemsInCart={numberOfItemsInCart}
          serviceMode={serviceMode}
          navigate={navigate}
          itemId={_id}
        />
      </FooterContainer>
    </View>
  );
};

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    flex: 1,
  },
  lottie: {
    height: '100%',
    width: '100%',
  },
});

const DisclaimerText = Text.withConfig({
  fontSize: '9px',
  marginBottom: '$2',
  marginLeft: '$5',
  alignSelf: 'flex-start',
});

const FooterContainer = Box.withConfig({
  paddingX: '$4',
  paddingY: '$4',
});

export const SimpleHomePageMobile = React.memo(_SimpleHomePageMobile);
